import { doDefaultFetch } from '@nhl/fetch-helpers';
import { stringFormatter } from '@nhl/formatter-utilities';

import { sortByName } from './sort';

const fetchTeamsForLeagueAndSeason = args => {
    return doDefaultFetch(`/site/api/team` +
        `?cayenneExp=stats.season=${args.season} and league.abbreviation='${args.league}'` +
        '&sort=fullName' +
        '&include=id' +
        '&include=triCode' +
        '&include=logos' +
        '&include=fullName' +
        '&include=placeName' +
        '&include=commonName' +
        '&include=arenaName' +
        '&include=arenaAddress' +
        '&include=arenaCity' +
        '&include=arenaStateProvince' +
        '&include=arenaZipPostalCode' +
        '&include=arenaCountry' +
        '&include=officeAddress' +
        '&include=officeAddress2' +
        '&include=officeCity' +
        '&include=officeStateProvince' +
        '&include=officeZipPostalCode' +
        '&include=officeCountry' +
        '&include=phoneNumber' +
        '&include=division.id' +
        '&include=division.name' +
        '&include=conference.id' +
        '&include=conference.name' +
        `&include={"path":"stats", "cayenneExp":"season=${args.season}"}` +
        '&include=stats.id' +
        '&include=stats.division.id' +
        '&include=stats.division.name' +
        '&include=stats.conference.id' +
        '&include=stats.conference.name' +
        '&include=stats.season.id',
        args);
    /*
     include=stats.division* and include=stats.conference* are used to get the teams division/conference for the specific season
     The generic include=division* and include=conference* are the teams CURRENT division/conference.
     */
};

const fetchActiveNHLTeams = args => (
    doDefaultFetch(
        `/site/api/team` +
        `?cayenneExp=active='Y' and league.abbreviation='NHL'` +
        '&sort=fullName' +
        '&include=id' +
        '&include=triCode' +
        '&include=logos' +
        '&include=fullName' +
        '&include=placeName' +
        '&include=commonName' +
        '&include=arenaName' +
        '&include=arenaAddress' +
        '&include=arenaCity' +
        '&include=arenaStateProvince' +
        '&include=arenaZipPostalCode' +
        '&include=arenaCountry' +
        '&include=officeAddress' +
        '&include=officeAddress2' +
        '&include=officeCity' +
        '&include=officeStateProvince' +
        '&include=officeZipPostalCode' +
        '&include=officeCountry' +
        '&include=phoneNumber' +
        '&include=division.id' +
        '&include=division.name' +
        '&include=conference.id' +
        '&include=conference.name',
        args
    )
);

const fetchGamesByTeamId = args => {
    return doDefaultFetch(`/site/api/team` +
        `?cayenneExp=id=${args.id}` +
        `&include={"path":"awayGames","cayenneExp":"id>${args.minimumGameId}"}` +
        `&include={"path":"homeGames","cayenneExp":"id>${args.minimumGameId}"}` +
        `&include=id` +
        `&include=triCode` +
        `&include=logos` +
        `&include=awayGames.id` +
        `&include=awayGames.state.id` +
        `&include=awayGames.gameDate` +
        `&include=awayGames.startTime` +
        `&include=awayGames.period` +
        `&include=awayGames.timeRemaining` +
        `&include=awayGames.easternStartTime` +
        `&include=awayGames.homeScore` +
        `&include=awayGames.visitingScore` +
        `&include=awayGames.homeTeam.id` +
        `&include=awayGames.homeTeam.triCode` +
        `&include=awayGames.homeTeam.logos` +
        `&include=awayGames.homeTeam.fullName` +
        `&include=awayGames.notes` +
        `&include=homeGames.id` +
        `&include=homeGames.state.id` +
        `&include=homeGames.gameDate` +
        `&include=homeGames.startTime` +
        `&include=homeGames.period` +
        `&include=homeGames.timeRemaining` +
        `&include=homeGames.easternStartTime` +
        `&include=homeGames.homeScore` +
        `&include=homeGames.visitingScore` +
        `&include=homeGames.visitingTeam.id` +
        `&include=homeGames.visitingTeam.triCode` +
        `&include=homeGames.visitingTeam.logos` +
        `&include=homeGames.visitingTeam.fullName` +
        `&include=homeGames.notes`,
        args);
};

const fetchTeamLogoAssets = args => {
    return doDefaultFetch(`/site/api/logo/` +
        `?include=team.id` +
        `&include=team.triCode` +
        `&include=team.commonName` +
        `&include=team.placeName` +
        `&include=team.franchise.id` +
        `&include=team.league.id`,
        args);
};

const fetchTeamLogoAssetsBySeason = args => {
    return doDefaultFetch(`/site/api/logo/` +
        `?cayenneExp=startSeason <= ${args.season} and endSeason >= ${args.season}` +
        `&include=team.id` +
        `&include=team.triCode` +
        `&include=team.commonName` +
        `&include=team.placeName` +
        `&include=team.franchise.id` +
        `&include=team.league.id`,
        args);
};

const fetchTeamSocialAccounts = args => {
    return doDefaultFetch(`/site/api/team/social` +
        `?include=team.twitter`,
        args
    );
};

const addTeamSlugs = teams => teams.map(team => {
    const slugProp = {
        slug: encodeURI(`${stringFormatter.toSlug(team.fullName)}`)
    };
    return Object.assign(team, slugProp);
});

/*
    @return
    An array of division objects.
    Each division object contains an id, name and alpha sorted teams array.

    divisions: [
        {
            id: 15,
            name: "Pacific",
            teams: [{}...]
        },
        ...
    ]
 */
const getTeamsByDivision = (teams) => {
    const divisions = [];

    if(teams) {
        teams.forEach(team => {
            // This check is here for when we have a new team, which has a division, but has not been given a TeamStats
            // row yet (e.g. VGK circa 2016)
            const teamDivision = team.stats ? team.stats.division : team.division;

            // Create a division object if it doesn't exist in the divisions array.
            if (!divisions.some(division => division.id === teamDivision.id)){
                divisions.push({
                    id: teamDivision.id,
                    name: teamDivision.name,
                    teams: []
                });
            }
            // Add team to the matching division objects team array
            // TODO: Make this a map, not a push
            // TODO: Have TeamsByDivision take a ...funcs
            divisions.find(division => division.id === teamDivision.id).teams.push(team);
        });

        // Sort each divisions team array by team name
        divisions.forEach(division => {
            division.teams = sortByName(division.teams, 'placeName')
        });
    }

    // Return the divisions in order of division name.
    return sortByName(divisions, 'name');
};

/*
    @return
    An array of conference objects.
    Each conference object contains an id, name and alpha sorted divisions array.

    conferences: [
        {
            id: 5,
            name: "Western,
            divisions: [
                {
                    id: 15,
                    name: "Pacific",
                    teams: [{}...]
                },
                ...
            ]
        },
        ...
    ]
 */
const getTeamsByConference = (teams) => {
    const conferences = [];

    if(teams) {
        const divisions = getTeamsByDivision(teams);

        divisions.forEach(division => {
            // This check is here for when we have a new team, which has a conference, but has not been given a TeamStats
            // row yet (e.g. VGK circa 2016)
            const divisionTeam = division.teams[0];
            const divisionConference = divisionTeam.stats ? divisionTeam.stats.conference : divisionTeam.conference;

            // Create a conference object if it doesn't exist in the conferences array.
            if(!conferences.some(conference => conference.id === divisionConference.id)){
                conferences.push({
                        id: divisionConference.id,
                        name: divisionConference.name,
                        divisions: []
                });
            }

            // Add division to the matching conference objects divisions array
            conferences.find(conference => conference.id === divisionConference.id).divisions.push(division);
        });
    }
    // Return the conferences in order of conference name.
    return sortByName(conferences, 'name');
};

/*
    @return
    A division object with each division array by divisionId.
    Each division array contains a the associated alpha sorted team objects for the division.

    teamsByDivision: {
        15: [ 21, 18, 4, 12 ],
        16: [ ... ],
        17: [ ... ],
        18: [ ... ]
    }

 */
const getTeamIdsByDivision = (divisions) => {
      if(divisions) {
          const teamIdsByDivision = {};

          Object.keys(divisions).forEach((division) => {
              teamIdsByDivision[division] = [];
              divisions[division].forEach((team) => {
                  teamIdsByDivision[division].push(team.id);
              });
          });

          return teamIdsByDivision;
      }
};

const getTeamById = (teams, teamId) => teams.find(team => team.id === teamId);

const getTeamBySlug = (teams, slug) => teams.find(team => team.slug === slug);


/*
TODO: getTeamsWithCorrectStats needs to be named better. As well as refactored to be more concise.
 */
const getTeamsWithCorrectStats = (teams, active, seasonId) => {
    return (
        teams.map(team => {
            if (team.stats) {
                if (active) {
                    team.stats = team.stats[team.stats.length - 1];
                } else {
                    team.stats = team.stats.find(stat => seasonId === stat.season.id);
                }
            }

            return team;
        })
    );
};

export default {
    fetchTeamsForLeagueAndSeason,
    fetchActiveNHLTeams,
    fetchGamesByTeamId,
    fetchTeamLogoAssets,
    fetchTeamLogoAssetsBySeason,
    fetchTeamSocialAccounts,
    getTeamIdsByDivision,
    addTeamSlugs,
    getTeamsByDivision,
    getTeamsByConference,
    getTeamById,
    getTeamBySlug,
    getTeamsWithCorrectStats
};
